import { Matrix } from "@awayjs/core";
import { ImageSampler, AttributesView, Float2Attributes, } from "@awayjs/stage";
import { MappingMode, Style } from "@awayjs/renderer";
import { TriangleElements } from "../elements/TriangleElements";
import { Shape } from "../renderables/Shape";
import { GraphicsFillStyle } from "./GraphicsFillStyle";
import { GradientFillStyle } from "./GradientFillStyle";
import { BitmapFillStyle } from "./BitmapFillStyle";
import { GradientType } from "./GradientType";
import { GraphicsFactoryHelper } from "./GraphicsFactoryHelper";
import { Graphics } from "../Graphics";
import Tess2 from "tess2";
/**
 * The Graphics class contains a set of methods that you can use to create a
 * vector shape. Display objects that support drawing include Sprite and Shape
 * objects. Each of these classes includes a <code>graphics</code> property
 * that is a Graphics object. The following are among those helper functions
 * provided for ease of use: <code>drawRect()</code>,
 * <code>drawRoundRect()</code>, <code>drawCircle()</code>, and
 * <code>drawEllipse()</code>.
 *
 * <p>You cannot create a Graphics object directly from ActionScript code. If
 * you call <code>new Graphics()</code>, an exception is thrown.</p>
 *
 * <p>The Graphics class is final; it cannot be subclassed.</p>
 */
var GraphicsFactoryFills = /** @class */ (function () {
    function GraphicsFactoryFills() {
    }
    GraphicsFactoryFills.draw_pathes = function (targetGraphics) {
        //return;
        var len = targetGraphics.queued_fill_pathes.length;
        var cp = 0;
        for (cp = 0; cp < len; cp++) {
            var newBuffer = GraphicsFactoryFills.pathToAttributesBuffer(targetGraphics.queued_fill_pathes[cp]);
            if (newBuffer && newBuffer.length > 0) {
                var elements = new TriangleElements(newBuffer);
                elements.setPositions(new Float2Attributes(newBuffer));
                //elements.setCustomAttributes("curves", new Float3Attributes(attributesBuffer));
                //elements.setUVs(new Float2Attributes(attributesBuffer));
                var sampler;
                var style;
                var material;
                if (targetGraphics.queued_fill_pathes[cp].style.data_type == GraphicsFillStyle.data_type) {
                    var obj = Graphics.get_material_for_color(targetGraphics.queued_fill_pathes[cp].style.color, targetGraphics.queued_fill_pathes[cp].style.alpha);
                    material = obj.material;
                    var shape = targetGraphics.addShape(Shape.getShape(elements, material));
                    if (obj.colorPos) {
                        shape.style = new Style();
                        sampler = new ImageSampler();
                        material.animateUVs = true;
                        shape.style.addSamplerAt(sampler, material.getTextureAt(0));
                        shape.style.uvMatrix = new Matrix(0, 0, 0, 0, obj.colorPos.x, obj.colorPos.y);
                    }
                }
                else if (targetGraphics.queued_fill_pathes[cp].style.data_type == GradientFillStyle.data_type) {
                    var gradientStyle = (targetGraphics.queued_fill_pathes[cp].style);
                    var obj = Graphics.get_material_for_gradient(gradientStyle);
                    material = obj.material;
                    var shape = targetGraphics.addShape(Shape.getShape(elements, material));
                    shape.style = new Style();
                    sampler = new ImageSampler();
                    shape.style.addSamplerAt(sampler, material.getTextureAt(0));
                    material.animateUVs = true;
                    shape.style.uvMatrix = gradientStyle.getUVMatrix();
                    if (gradientStyle.type == GradientType.LINEAR)
                        material.getTextureAt(0).mappingMode = MappingMode.LINEAR;
                    else if (gradientStyle.type == GradientType.RADIAL) {
                        sampler.imageRect = gradientStyle.uvRectangle;
                        material.imageRect = true;
                        material.getTextureAt(0).mappingMode = MappingMode.RADIAL;
                    }
                }
                else if (targetGraphics.queued_fill_pathes[cp].style.data_type == BitmapFillStyle.data_type) {
                    var bitmapStyle = targetGraphics.queued_fill_pathes[cp].style;
                    var material = bitmapStyle.material; //new ITexture(ImageUtils.getDefaultImage2D());//bitmapStyle.texture;
                    var shape = targetGraphics.addShape(Shape.getShape(elements, material));
                    shape.style = new Style();
                    sampler = new ImageSampler();
                    sampler.mipmap = true;
                    sampler.smooth = true;
                    sampler.repeat = bitmapStyle.repeat;
                    shape.style.addSamplerAt(sampler, material.getTextureAt(0));
                    material.animateUVs = true;
                    shape.style.uvMatrix = bitmapStyle.getUVMatrix();
                }
            }
        }
        targetGraphics.queued_fill_pathes.length = 0;
    };
    GraphicsFactoryFills.pathToAttributesBuffer = function (graphicsPath, closePath) {
        if (closePath === void 0) { closePath = true; }
        graphicsPath.prepare();
        var contour_data = graphicsPath._positions;
        var contour;
        var i = 0;
        var k = 0;
        var final_vert_list = [];
        var final_vert_cnt = 0;
        var finalContours = [];
        var res;
        var numElems = 0;
        var p1x = 0;
        var p1y = 0;
        var p2x = 0;
        var p2y = 0;
        var p3x = 0;
        var p3y = 0;
        var attributesView;
        var attributesBuffer;
        var tessFix = 20;
        var fixedPoints = 3;
        var fixedBase = Math.pow(10, fixedPoints);
        var eps = 1 / fixedBase;
        var useTessFix = true;
        var toFixed = function (value) {
            return Math.round(value * fixedBase) / fixedBase;
        };
        var nearestCheck = function (ax, ay, bx, by) {
            // manhattan distance, fast check for nearest point
            var mah = Math.abs(ax - bx) + Math.abs(ay - by);
            return mah <= eps;
        };
        for (var k_1 = 0; k_1 < contour_data.length; k_1++) {
            var contour_1 = contour_data[k_1];
            // same as map, but without allocation 
            var closed_1 = nearestCheck(contour_1[0], contour_1[1], contour_1[contour_1.length - 2], contour_1[contour_1.length - 1]);
            // make sure start and end point of a contour are not the same
            if (closed_1) {
                contour_1.pop();
                contour_1.pop();
            }
            // all contours should already be prepared by GraphicsPath.prepare()
            // we only want to make sure that each contour contains at least 3 pairs of x/y positions
            // otherwise there is no way they can form a shape
            if (contour_1.length > 5) {
                if (useTessFix) {
                    // tess2 fix
                    // there are problems with small shapes
                    var fixed = contour_1.map(function (e) { return toFixed(e) * tessFix; });
                    finalContours.push(fixed);
                }
                else {
                    finalContours.push(contour_1);
                }
            }
        }
        //console.log("execute Tess2 = ", finalContours);
        try {
            res = Tess2.tesselate({
                contours: finalContours,
                windingRule: Tess2.WINDING_ODD,
                elementType: Tess2.POLYGONS,
                polySize: 3,
                vertexSize: 2,
                debug: true
            });
            //console.timeEnd("time Tess2.tesselate");
            numElems = res.elements.length / 3;
            p1x = 0;
            p1y = 0;
            p2x = 0;
            p2y = 0;
            p3x = 0;
            p3y = 0;
            for (i = 0; i < numElems; ++i) {
                p1x = res.vertices[res.elements[i * 3] * 2];
                p1y = res.vertices[res.elements[i * 3] * 2 + 1];
                p2x = res.vertices[res.elements[i * 3 + 1] * 2];
                p2y = res.vertices[res.elements[i * 3 + 1] * 2 + 1];
                p3x = res.vertices[res.elements[i * 3 + 2] * 2];
                p3y = res.vertices[res.elements[i * 3 + 2] * 2 + 1];
                if (GraphicsFactoryHelper.isClockWiseXY(p1x, p1y, p2x, p2y, p3x, p3y)) {
                    final_vert_list[final_vert_cnt++] = p3x;
                    final_vert_list[final_vert_cnt++] = p3y;
                    final_vert_list[final_vert_cnt++] = p2x;
                    final_vert_list[final_vert_cnt++] = p2y;
                    final_vert_list[final_vert_cnt++] = p1x;
                    final_vert_list[final_vert_cnt++] = p1y;
                }
                else {
                    final_vert_list[final_vert_cnt++] = p1x;
                    final_vert_list[final_vert_cnt++] = p1y;
                    final_vert_list[final_vert_cnt++] = p2x;
                    final_vert_list[final_vert_cnt++] = p2y;
                    final_vert_list[final_vert_cnt++] = p3x;
                    final_vert_list[final_vert_cnt++] = p3y;
                }
            }
        }
        catch (e) {
            console.log("error when trying to tesselate", finalContours);
        }
        //}
        if (useTessFix) {
            // divide to avoid increase shape size
            final_vert_list.forEach(function (e, i) { return final_vert_list[i] = e / tessFix; });
        }
        final_vert_list = final_vert_list.concat(graphicsPath.verts);
        if (final_vert_list.length > 0) {
            attributesView = new AttributesView(Float32Array, 2);
            attributesView.set(final_vert_list);
            attributesBuffer = attributesView.attributesBuffer.cloneBufferView();
            attributesView.dispose();
            return attributesBuffer;
        }
        return null;
    };
    return GraphicsFactoryFills;
}());
export { GraphicsFactoryFills };
